/**
 * Utility for wrapping promises with timeouts
 * Prevents async operations from hanging indefinitely
 */

export class TimeoutError extends Error {
    constructor(message = 'Operation timed out') {
        super(message);
        this.name = 'TimeoutError';
    }
}

/**
 * Wraps a promise with a timeout
 * @param promise - The promise to wrap
 * @param ms - Timeout in milliseconds
 * @param errorMessage - Custom error message for timeout
 * @returns The result of the promise or throws TimeoutError
 */
export async function withTimeout<T>(
    promise: Promise<T>,
    ms: number,
    errorMessage = 'Operation timed out'
): Promise<T> {
    let timeoutId: NodeJS.Timeout;

    const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            reject(new TimeoutError(errorMessage));
        }, ms);
    });

    try {
        const result = await Promise.race([promise, timeoutPromise]);
        clearTimeout(timeoutId!);
        return result;
    } catch (error) {
        clearTimeout(timeoutId!);
        throw error;
    }
}

/**
 * Wraps a promise with a timeout that returns a default value on timeout
 * instead of throwing an error
 * @param promise - The promise to wrap
 * @param ms - Timeout in milliseconds  
 * @param defaultValue - Value to return on timeout
 * @returns The result of the promise or the default value
 */
export async function withTimeoutDefault<T>(
    promise: Promise<T>,
    ms: number,
    defaultValue: T
): Promise<T> {
    try {
        return await withTimeout(promise, ms);
    } catch (error) {
        if (error instanceof TimeoutError) {
            console.warn(`⏱️ Operation timed out after ${ms}ms, using default value`);
            return defaultValue;
        }
        throw error;
    }
}

/**
 * Retries a promise with exponential backoff
 * @param fn - Function that returns a promise
 * @param maxRetries - Maximum number of retries
 * @param baseDelay - Base delay in ms (doubles each retry)
 * @returns The result of the promise
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    maxRetries = 3,
    baseDelay = 1000
): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error as Error;
            if (attempt < maxRetries - 1) {
                const delay = baseDelay * Math.pow(2, attempt);
                console.warn(`⚠️ Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    throw lastError;
}
